import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(null); // RAW User object

  useEffect(() => {
    // onAuthStateChanged se ejecuta al cargar y cada vez que el auth cambia
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser); // Save actual user object
      let docUnsubscribe = () => { }; // Función vacía para limpiar el snapshot
      let gracePeriodTimer = null; // Temporizador para el período de gracia

      if (authUser) {
        // --- Usuario HA INICIADO SESIÓN ---
        const profileDocRef = doc(db, 'usuarios', authUser.uid);

        // --- LÓGICA DE PERÍODO DE GRACIA ---
        // Si el documento no aparece en 3 segundos, asumimos que es un error
        // y dejamos de cargar.
        gracePeriodTimer = setTimeout(() => {
          console.error("Timeout: El perfil del usuario no se encontró después de 3s.");
          setUserData(null);
          setLoading(false);
        }, 3000); // 3 segundos de "período de gracia"

        docUnsubscribe = onSnapshot(profileDocRef, (docSnap) => {
          // Si el documento existe, ¡perfecto!
          if (docSnap.exists()) {
            clearTimeout(gracePeriodTimer); // Cancelamos el temporizador de error

            const data = docSnap.data();
            const mappedData = {
              uid: authUser.uid,
              email: authUser.email,
              // Mapeo de los campos de tu DB a los campos de la UI
              avatarUrl: data.fotoURL || null, // de 'fotoURL' a 'avatarUrl'
              fullName: data.nombre || '',      // de 'nombre' a 'fullName'
              username: data.username || '',  // 'username' es igual
              tradingActivado: data.tradingActivado || false, //revisa activación del apartado Trading Automatizado
              emailVerified: authUser.emailVerified, // Estado de verificación de correo
              walletAddress: data.walletAddress || null, // 0x...
              verificationStatus: data.verificationStatus || 'unverified', // unverified | pending | verified
              bankAccount: data.bankAccount || null,
              verificationAmount: data.verificationAmount || 0,
              verificationAttempts: data.verificationAttempts || 0,
              walletHistory: data.walletHistory || [], // Historical wallets for referral tracking
              referredBy: data.referredBy || null // <--- Added: Expose referrer info
            };

            setUserData(mappedData);
            setLoading(false);
          }
          // --- CAMBIO CRÍTICO ---
          // Si el documento NO existe, NO hacemos nada.
          // Simplemente dejamos que el 'loading' siga en 'true'.
          // O el 'gracePeriodTimer' se encargará, o el 'onSnapshot'
          // se volverá a disparar cuando la Cloud Function termine.
        }, (error) => {
          // Si hay un error de Firestore
          clearTimeout(gracePeriodTimer); // Cancelamos el temporizador
          console.error("Error al escuchar el perfil:", error);
          setUserData(null);
          setLoading(false);
        });

      } else {
        // --- Usuario HA CERRADO SESIÓN ---
        setUserData(null);
        setLoading(false);
      }

      // Limpiamos el "oyente" del documento cuando el auth cambia
      return () => {
        docUnsubscribe();
        if (gracePeriodTimer) clearTimeout(gracePeriodTimer); // Limpiamos el timer
      };
    });

    // Limpiamos el "oyente" de auth cuando el componente se desmonta
    return () => unsubscribeAuth();
  }, []);

  const updateUserData = (newUserData) => {
    setUserData(prevData => ({ ...prevData, ...newUserData }));
  };

  // Función para VINCULAR una billetera permanentemente al perfil
  // DEPRECATED: Use Web3Context.manualBindWallet instead. 
  // We keep the signature to avoid breaking imports, but it does nothing.
  const bindWallet = async (address) => {
    console.warn("UserContext.bindWallet is DEPRECATED. Execution blocked to prevent auto-binding loops.");
    console.warn("Please use the 'Vincular' button in ProfileView.");
    return;
  };

  // Función para VERIFICAR si una wallet ya tiene dueño
  const checkWalletOwner = async (address) => {
    try {
      const { collection, query, where, getDocs } = await import("firebase/firestore");
      const usersRef = collection(db, 'usuarios');
      const q = query(usersRef, where("walletAddress", "==", address));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Ya existe un dueño
        const ownerDoc = querySnapshot.docs[0];
        return ownerDoc.id; // Retorna el UID del dueño
      }
      return null; // Libre
    } catch (error) {
      console.error("Error verificando dueño de wallet:", error);
      return null;
    }
  };

  const value = {
    user, // <--- EXPORTED NOW
    userData,
    updateUserData,
    bindWallet, // Exportamos la función
    checkWalletOwner, // Exportamos la función
    loading,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};

